import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : react hooks for db interaction
// =============================================================================
// ---Third-party imports
import React, { createContext, useContext } from 'react';
// ---Framework imports
import { Sel } from 'uix/registries';
import { size, sorted, values, get, isNil, dict, mapItems, someItems, isString, callIfFunc, keys, items, flatten, isObject, isInteger, isDigit, isArray, uniq } from 'ldash';
import log from 'uix/logging';
import { hashObj, ucFirst } from 'uix/utils';
import { useSelector, useActions, useMemo, useApi, useEffectSSR as useEffect, useState, usePartContext } from 'uix/hooks';
// ---Local imports
import { registerColumns, unregisterColumns, configureColumns, getNoDataColumns, ColumnsById } from './columns';
import { ColumnTypeNames, ColHeader } from './columnTypes';
// ---Exports
export const TreelistContext = /*#__PURE__*/ createContext({});
export const useTreelistContext = ()=>useContext(TreelistContext);
export const useCustomTreeList = (name, params = {}, resolveLinks = false, enabled = true)=>{
    let listid = name;
    params = {
        ...params,
        customListName: name
    };
    return useTreeList(listid, params, resolveLinks, enabled);
};
export const useTreeList = (listid, params = {}, resolveLinks = false, enabled = true)=>{
    /* generic tree data fetcher
    @listid: name in redux store

    @params controls the list filtering:
    dname=None # use this domain context (materialize as path prefix)
    parentid=None # fetch nodes below this is
    rootFolder=None # some container parentids can be reached by name (and domain context)
    subdomains=False # fetch subdomains (if not parentid given)
    descendants=False # fetch all descendants (if parentid given)
    includeParent=False # include parent in result set
    typeName=None # list of type names
    stateNames=None # list of released:released,qa:approved
    links=True # fetch fat-links
    deleted=False # fetch deleted records
    limit=100000 # limit result
    otids # direct list of otids
    # only optimization
    parentType=None # need one type db-lookup less if given next to parentid
  */ let listData = useSelector((root)=>root.data.db.list[listid]);
    let ll = useSelector((root)=>root.data.db.list);
    let listLoadingInfo = useSelector((root)=>root.data.db.list_loading[listid]) || {};
    let listLoadingParams = listLoadingInfo.params;
    let listLoading = listLoadingInfo.loading === true;
    let listLoadingError = listLoadingInfo.error;
    let request = useSelector(Sel.request);
    let fetchTreeList = useActions('fetchTreeList');
    params = {
        ...params,
        dname: request.dname
    };
    let paramsKey = hashObj(params);
    useEffect(()=>{
        if (enabled && listLoadingParams !== paramsKey) {
            fetchTreeList(listid, params, paramsKey);
        }
    }, [
        enabled,
        paramsKey
    ]);
    // resolveLinks: transform first linked row (of otid) to physical
    // ignore more links
    let resolvedListData = useMemo(()=>{
        let res = [];
        if (listData) {
            if (!resolveLinks) {
                res = listData;
            } else {
                let resolved = {};
                for (let e of listData){
                    if (e.otlink) {
                        if (!resolved[e.otlink]) {
                            res.push({
                                ...e,
                                otid: e.otlink,
                                odid: e.odlink,
                                otlink: null
                            });
                            resolved[e.otlink] = true;
                        }
                    } else if (!resolved[e.otid]) {
                        res.push(e);
                        resolved[e.otid] = true;
                    }
                }
            }
        }
        return res;
    }, [
        listData,
        resolveLinks
    ]);
    let treeListOpts = {
        types: params.typeName,
        subdomains: !!params.subdomains,
        links: !!params.links,
        descendants: !!params.descendants
    };
    return {
        treeList: {
            list: resolvedListData,
            options: treeListOpts,
            count: resolvedListData.length
        },
        loading: listLoading,
        error: listLoadingError
    };
};
export const useDBFieldsLoading = (otherLoading)=>{
    /* combine given loading indicator
  with other tracked api activity
  XXX: we need a part context here
  */ let activity = useSelector((root)=>root.data.db.activity);
    let TRACKED_ACTIONS = [
        //~ 'FETCH_TREE_DESCENDANTS',
        //~ 'GET_TREE_CHILDREN',
        'GET_TREE_RECORDS'
    ];
    return otherLoading || someItems(activity, (name, value)=>TRACKED_ACTIONS.includes(name) && value);
};
export const useTreeRowData = (treeNodes, fields, batchId)=>{
    /* return fields (column ids) for tree nodes [{otid, datatype}, ...]
  returns unordered list with detail records (root.data.tree.byId)
  resulting detail records are *not* filtered down by fields (contain all data that has been fetched up til now)
  */ let byID = useSelector((root)=>root.data.db.byId);
    let getTreeRecords = useActions('getTreeRecords');
    // trigger getTreeRecords action if input params change
    useEffect(()=>{
        if (size(treeNodes) > 0 && fields.length > 0) {
            // before considering optimizations here: getTreeRecords reuses already fetched rows
            getTreeRecords(treeNodes, fields, batchId);
        }
    }, [
        treeNodes,
        hashObj(fields),
        batchId
    ]);
    // build result by picking records from byID structure
    let rowData = useMemo(()=>{
        if (treeNodes) {
            return treeNodes.map((e)=>({
                    ...e,
                    ...byID[e.otid] ?? {}
                }));
        }
    }, [
        treeNodes,
        byID
    ]);
    return rowData;
};
let NODE_FIELDS = [
    'odid',
    'datatype',
    'subtype',
    'caption'
];
export const useTreeNodesRaw = (otids, { validate, fields, datatype = -1 } = {})=>{
    /* given list of otids, fetch basic node info from server
  return list [{otid, odid, datatype, subtype, caption}, ...]
  */ let node_fields = fields || NODE_FIELDS;
    let onlyValid = (otid)=>isInteger(otid) || isDigit(otid);
    if (datatype == -1 && node_fields.some((f)=>f.includes('.'))) {
        log.error('useTreeNodesRaw(): namespaced fields need datatype info ');
    }
    let listData = useMemo(()=>{
        let ids = uniq(otids);
        return (validate ? ids.filter(onlyValid) : ids).map((otid)=>({
                otid,
                datatype
            }));
    }, [
        hashObj(otids),
        validate
    ]);
    // before considering optimizations here: underlying getTreeRecords reuses already fetched rows
    let res = useTreeRowData(listData, node_fields);
    return useMemo(()=>res.filter((e)=>e.datatype !== -1), [
        res
    ]);
};
export const useTreeNodes = (otids, options = {})=>{
    /* given list of otids, fetch basic node info from server
  the underlying implementation will prevent superfluous fetches
  if options.validate, check otids for integer form
  return mapping {otid: {otid, odid, datatype, subtype, caption}}

  Attention: datatype=-1 allows only OT_* fields
   */ let nodes = useTreeNodesRaw(otids, options);
    return useMemo(()=>dict(nodes.map((n)=>[
                n.otid,
                n
            ])), [
        nodes
    ]);
};
// scalar version of useTreeNodes, returns undefined on invalid input
export const useTreeNode = (otid, options)=>useTreeNodes([
        otid
    ], {
        validate: true,
        ...options
    })[otid];
// get some custom fields for a tree node ({otid, datatype})
export const useTreeNodeFields = (node, fields)=>useTreeNode(node.otid, {
        fields,
        datatype: node.datatype
    });
export const useMetadataDBFields = (otid, fields)=>{
    /* given a metadata otid, fetch given fields
  return mapping {field1: value1, ...}
   */ let listData = useMemo(()=>[
            {
                otid,
                datatype: 1
            }
        ], [
        otid
    ]);
    let records = useTreeRowData(listData, fields);
    return useMemo(()=>dict(mapItems(records[0] || {}, // this '.' to '_' looks pretty ugly.
        (k, v)=>[
                k.replace('_', '.'),
                v
            ], (k)=>fields.includes(k.replace('_', '.')))), [
        records
    ]);
};
export const indexRows = (rows, itypes)=>{
    let odids = new Set();
    let typeNames = new Set();
    let datatypes = new Set();
    rows.forEach((r)=>{
        odids.add(r.odid);
        if (r.odlink) {
            odids.add(r.odlink);
        }
        typeNames.add(itypes.byId[r.datatype][r.subtype]);
        datatypes.add(r.datatype);
        if (r.parent_rcv_datatype) {
            //~ datatypes.add(r.parent_rcv_datatype)
            if (r.parent_rcv_type) {
                typeNames.add(itypes.byId[r.parent_rcv_datatype][r.parent_rcv_type]);
            }
        }
    });
    return {
        odids: [
            ...odids
        ],
        // seen type names (also includes parent field)
        typeNames: [
            ...typeNames
        ].filter((e)=>e).map((e)=>e.replace(/ /g, '_')),
        // active datatypes (only primary rows)
        datatypes: [
            ...datatypes
        ]
    };
};
export const useColumnsConfig = ()=>{
    let { dname, i18n, user } = usePartContext();
    let itypes = useSelector(Sel.itypes);
    let col_env = {
        dname,
        i18n,
        domains: useSelector(Sel.domains),
        itypes,
        activeDescendantTypes: useSelector(Sel.activeDescendantTypes),
        user,
        apiurl: useSelector(Sel.apiurl),
        // dummies
        rowIndex: indexRows([], itypes),
        treeListOpts: {}
    };
    return useMemo(()=>dict(configureColumns(col_env).map((col)=>[
                col.id,
                col
            ])), values(col_env));
};
/* given a internalColumns representation (with header groups and checkbox cols)
return flat list of real columns
*/ export const getInternalColumnCfgs = (internalColumns)=>[
        ...internalColumns.filter((c)=>!c.columns && c.id != 'checkbox'),
        ...flatten(internalColumns.filter((c)=>c.columns).map((c)=>c.columns))
    ];
export const buildCellData = (colid, cfg, data)=>{
    /* render cell from column cfg

   */ let value = null;
    if (isString(cfg.accessor)) {
        value = data[cfg.accessor];
    } else {
        value = cfg.accessor(data);
    }
    let rendered = cfg.Cell({
        value,
        original: data
    });
    //~ let renderedLabel = callIfFunc(cfg.Header)
    let i18nKey = cfg.i18nKey || `col_${colid}` // as in <ColHeader/>
    ;
    return {
        value,
        colns: cfg.namespace,
        i18nKey,
        rendered,
        buildLabel: (namespaced = false)=>/*#__PURE__*/ _jsx(ColHeader, {
                colid: colid,
                i18nKey: cfg.i18nKey,
                namespace: namespaced ? cfg.namespace : null
            })
    };
};
export const getFieldsForColid = (colid)=>{
    let opts = ColumnsById[colid]?.options || {};
    // BooleanColumn+DownloadLinkColumn uses scalar `field` and MultiFieldColumn uses list `fields`
    return opts.fields || (opts.field ? [
        opts.field
    ] : [
        colid
    ]);
};
export const useColumns = ({ otid, odid, datatype, subtype, caption, colids })=>{
    /* given a single tree node, fetch fields/columns and render/build cells
   */ let nodeData = {
        otid,
        odid,
        datatype,
        subtype,
        caption
    };
    let ancestorAxis = useSelector(Sel.ancestorAxis);
    let trackedSkipped = useSelector((root)=>root.data.db.trackedSkips);
    let datatypeSkipped = new Set(trackedSkipped[datatype] || []);
    if (isNil(datatype)) {
        if (ancestorAxis[0]?.otid == otid) {
            nodeData = {
                ...ancestorAxis[0]
            };
        } else {
            log.warn('cannot derive datatype from otid+ancestoraxis: %s', otid);
        }
    }
    let columnConfig = useColumnsConfig();
    let noDataColumns = getNoDataColumns(values(columnConfig));
    let { listData, fetchFields } = useMemo(()=>{
        // resolve field names from 3 cases:
        // 1) columns without a field
        // 2) columns where colid is the field name
        // 3) columns with a explicit .fields list
        let fetchFields = flatten(colids.filter((colid)=>!noDataColumns.has(colid)).map(getFieldsForColid));
        return {
            listData: [
                nodeData
            ],
            fetchFields
        };
    }, [
        hashObj([
            nodeData,
            colids,
            noDataColumns
        ])
    ]);
    let rowData = useTreeRowData(listData, fetchFields);
    return useMemo(()=>{
        let columns = {};
        if (rowData.length == 1) {
            columns = dict(colids.map((colid)=>{
                let cfg = columnConfig[colid];
                if (!cfg) {
                    // fallback to minimal cfg for buildCellData
                    let data_fn = colid.replace('.', '_');
                    cfg = {
                        accessor: data_fn,
                        Cell: ()=>rowData[0][data_fn]
                    };
                }
                let data = {
                    ...nodeData,
                    ...rowData[0]
                };
                return [
                    colid,
                    buildCellData(colid, cfg, data)
                ];
            }));
        }
        // Yes: too fragile
        // probably fragile evaluation
        //~ let unloaded_cols = items(columns).filter(
        //~ ([colid, col]) => col.value === undefined && !datatypeSkipped.has(col.colns)
        //~ )
        //~ let loading = unloaded_cols.length > 0
        //~ log.x(columns, unloaded_cols)
        return {
            columns,
            loading: false
        };
    }, [
        columnConfig,
        rowData
    ]);
};
export const useTreeFieldSchema = ()=>{
    let schema = useSelector((root)=>root.data.db.schema);
    let [setTreeFieldSchema, errorNotification] = useActions([
        'setTreeFieldSchema',
        'errorNotification'
    ]);
    let { data, error } = useApi('treeFieldSchema', {}, {
        condition: size(schema) === 0
    });
    useEffect(()=>{
        if (data) {
            setTreeFieldSchema(data.schema);
        } else if (error) {
            errorNotification(error);
        }
    }, [
        data,
        error
    ]);
    return schema;
};
export const useColumnsForNode = ({ fields, nodeData, showEmpty = false } = {})=>{
    let { columns, loading } = useColumns({
        ...nodeData,
        colids: fields
    });
    let columnList = sorted(items(columns), ([colid, col])=>fields.indexOf(colid));
    let customFilter = (colid, value)=>{
        if (colid == 'caption' && size(value) === 0) {
            return false;
        }
        if (colid == 'op.paginationcaption' && size(value) === 0) {
            return false;
        }
        return true;
    };
    // multifield columns like op.dimensions_dpi
    let isEmptyDict = (value)=>isObject(value) && !values(value).some((v)=>!isNil(v));
    columnList = columnList.map(([colid, data])=>({
            ...data,
            id: colid
        }));
    columnList = columnList.filter(({ id, value })=>showEmpty || !isNil(value) && !isEmptyDict(value) && customFilter(id, value));
    return columnList;
};
export const useColumnRegistration = (new_cols, prevent_unregister)=>{
    /* register some cols given as [[coltype, colid, options], ...]
   */ if (isObject(new_cols)) {
        // register some simple text columns derived from namespace + fields
        if (new_cols.ns && new_cols.fields) {
            let full_names = new_cols.fields.map((f)=>`${new_cols.ns}.${f}`);
            new_cols = full_names.map((n)=>[
                    ColumnTypeNames.TextColumn,
                    n,
                    {
                        header: ucFirst(n.slice(new_cols.ns.length + 1)),
                        ...new_cols.options
                    }
                ]);
        } else {
            new_cols = [];
        }
    }
    let [ready, setReady] = useState(false);
    let hmr_gen = useSelector((root)=>root.debug.hmr.gen);
    // do it early, we want to have the mutation immediately
    if (!ready) {
        registerColumns(new_cols);
    }
    useEffect(()=>{
        registerColumns(new_cols);
        // but dont do it often
        setReady(true);
        return ()=>{
            if (!prevent_unregister) {
                unregisterColumns(new_cols);
            }
        };
    }, [
        hmr_gen,
        new_cols.length
    ]); // fishy length dependency
};
